import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';

export type ConsentCategories = {
  analytics: boolean;
  marketing: boolean;
};

export type ConsentState = ConsentCategories & {
  necessary: true;
};

const CONSENT_COOKIE = 'cookie_consent';
const CONSENT_VERSION = 1;

type StoredConsent = {
  v: number;
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

// Tacit consent default: analytics on unless the user explicitly rejects
const DEFAULT_CATEGORIES: ConsentCategories = {
  analytics: true,
  marketing: false,
};

const readStoredConsent = (): StoredConsent | null => {
  const raw = Cookies.get(CONSENT_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.v === CONSENT_VERSION) return parsed;
    return null;
  } catch {
    return null;
  }
};

const writeStoredConsent = (categories: ConsentCategories) => {
  const value: StoredConsent = { v: CONSENT_VERSION, necessary: true, ...categories };
  Cookies.set(CONSENT_COOKIE, JSON.stringify(value), { expires: 180 });
  return value;
};

type CookieConsentContextProps = {
  ready: boolean;
  hasChoice: boolean;
  consent: ConsentState;
  showBanner: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (categories: ConsentCategories) => void;
  openPreferences: () => void;
};

export const CookieConsentContext = React.createContext<CookieConsentContextProps>({
  ready: false,
  hasChoice: false,
  consent: { necessary: true, ...DEFAULT_CATEGORIES },
  showBanner: false,
  acceptAll: () => {},
  rejectAll: () => {},
  savePreferences: () => {},
  openPreferences: () => {},
});

const applyConsentToTrackers = (categories: ConsentCategories) => {
  window.dispatchEvent(
    new CustomEvent('cookie-consent-updated', { detail: categories })
  );
};

const CookieConsentContextProvider: React.FC = ({ children }) => {
  const [hasChoice, setHasChoice] = useState(false);
  const [categories, setCategories] = useState<ConsentCategories>(DEFAULT_CATEGORIES);
  const [showBanner, setShowBanner] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const stored = readStoredConsent();
    if (stored) {
      // User has already made an explicit choice — honour it
      const { analytics, marketing } = stored;
      setCategories({ analytics, marketing });
      setHasChoice(true);
      setShowBanner(false);
      applyConsentToTrackers({ analytics, marketing });
    } else {
      // No stored choice: apply tacit-consent defaults and show the informational banner
      applyConsentToTrackers(DEFAULT_CATEGORIES);
      setShowBanner(true);
    }
    setReady(true);
  }, []);

  const commit = (next: ConsentCategories) => {
    writeStoredConsent(next);
    setCategories(next);
    setHasChoice(true);
    setShowBanner(false);
    applyConsentToTrackers(next);
  };

  const acceptAll = () => commit({ analytics: true, marketing: true });
  const rejectAll = () => commit({ analytics: false, marketing: false });
  const savePreferences = (next: ConsentCategories) => commit(next);
  const openPreferences = () => setShowBanner(true);

  return (
    <CookieConsentContext.Provider
      value={{
        ready,
        hasChoice,
        consent: { necessary: true, ...categories },
        showBanner,
        acceptAll,
        rejectAll,
        savePreferences,
        openPreferences,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export default CookieConsentContextProvider;
